import React from 'react';

/**
 * Card Component - GCHL Brand styled container with multiple variants
 *
 * @param {Object} props
 * @param {boolean} props.interactive - Add hover effects for clickable cards
 * @param {boolean} props.gradient - Add golf gradient background (forest green)
 * @param {boolean} props.shine - Add premium shine hover effect
 * @param {'gold'|'seafoam'|'sunset'|'forest'|null} props.leftBorder - Colored left border accent for status indication:
 *   - 'gold': Top rankings, achievements, pending review (gold-400)
 *   - 'seafoam': Success states, birdies, positive (seafoam teal)
 *   - 'sunset': Warnings, alerts, bogeys, issues (sunset orange)
 *   - 'forest': Active/live states, brand accent (forest green)
 * @param {boolean} props.noPadding - Remove default padding
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.children - Card content
 * @param {Function} props.onClick - Click handler (makes card interactive)
 */
const Card = ({
  interactive = false,
  gradient = false,
  shine = false,
  leftBorder = null,
  noPadding = false,
  className = '',
  children,
  onClick,
  ...props
}) => {
  // Base styles
  const baseStyles = `
    bg-white dark:bg-navy-900
    rounded-lg shadow-card
    border border-sandtrap dark:border-gray-800
    transition-all duration-200
  `;

  // Padding (can be disabled)
  const paddingStyles = noPadding ? '' : 'p-4 md:p-6';

  // Gradient background (forest green - for premium/featured cards)
  const gradientStyles = gradient
    ? `
      bg-gradient-golf
      text-white border-none
      dark:bg-gradient-to-br dark:from-navy-800 dark:to-navy-900
    `
    : '';

  // Interactive hover effects
  const interactiveStyles = interactive || onClick
    ? `
      cursor-pointer
      hover:shadow-card-hover hover:scale-[1.02]
      active:scale-[0.99]
    `
    : '';

  // Shine hover effect (premium cards)
  const shineStyles = shine ? 'shine-hover overflow-hidden relative' : '';

  // Left border accent colors - for status/ranking indication
  // Uses GCHL brand semantic colors
  const leftBorderColors = {
    gold: 'border-l-4 border-l-gold-400',                                    // Rankings, achievements
    seafoam: 'border-l-4 border-l-seafoam-light dark:border-l-seafoam-dark', // Success, birdies
    sunset: 'border-l-4 border-l-sunset-light dark:border-l-sunset-dark',    // Warnings, bogeys
    forest: 'border-l-4 border-l-forest-700 dark:border-l-forest-400',       // Active, brand
  };

  const leftBorderStyles = leftBorder ? leftBorderColors[leftBorder] || '' : '';

  const combinedClassName = `
    ${baseStyles}
    ${paddingStyles}
    ${gradientStyles}
    ${interactiveStyles}
    ${shineStyles}
    ${leftBorderStyles}
    ${className}
  `.replace(/\s+/g, ' ').trim();

  return (
    <div
      className={combinedClassName}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(e);
        }
      } : undefined}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * CardHeader - Standardized card header with title and optional actions
 */
export const CardHeader = ({ title, subtitle, actions, className = '' }) => (
  <div className={`flex items-start justify-between mb-4 ${className}`}>
    <div>
      <h3 className="text-card-header text-navy-900 dark:text-navy-100">
        {title}
      </h3>
      {subtitle && (
        <p className="text-meta text-gray-500 dark:text-navy-400 mt-1">
          {subtitle}
        </p>
      )}
    </div>
    {actions && <div className="flex gap-2">{actions}</div>}
  </div>
);

/**
 * CardBody - Card content wrapper
 */
export const CardBody = ({ children, className = '' }) => (
  <div className={className}>{children}</div>
);

/**
 * CardFooter - Card footer with border separator
 */
export const CardFooter = ({ children, className = '' }) => (
  <div className={`border-t border-sandtrap dark:border-gray-800 pt-4 mt-4 ${className}`}>
    {children}
  </div>
);

export default Card;
